import { z } from 'zod';

// ─────────── Eventos da loja: feirão, saldão, test drive day… com promoções, gastos e resultado ───────────

export const TIPOS_EVENTO = [
  'feirao',
  'saldao',
  'test_drive',
  'lancamento',
  'acao_externa',
  'outro',
] as const;
export type TipoEvento = (typeof TIPOS_EVENTO)[number];
export const NOME_TIPO_EVENTO: Record<TipoEvento, string> = {
  feirao: 'Feirão',
  saldao: 'Saldão / queima de estoque',
  test_drive: 'Test drive day',
  lancamento: 'Lançamento / chegada de carros',
  acao_externa: 'Ação fora da loja (shopping, feira)',
  outro: 'Outro',
};

export const CATEGORIAS_GASTO_EVENTO = [
  'estrutura',
  'divulgacao',
  'alimentacao',
  'brindes',
  'equipe',
  'som_decoracao',
  'outros',
] as const;
export type CategoriaGastoEvento = (typeof CATEGORIAS_GASTO_EVENTO)[number];
export const NOME_GASTO_EVENTO: Record<CategoriaGastoEvento, string> = {
  estrutura: 'Estrutura (tenda, aluguel do espaço)',
  divulgacao: 'Divulgação (anúncios, carro de som, panfletos)',
  alimentacao: 'Comida e bebida',
  brindes: 'Brindes e sorteios',
  equipe: 'Equipe extra e horas extras',
  som_decoracao: 'Som, balões e decoração',
  outros: 'Outros',
};

export const STATUS_CONVIDADO = ['convidado', 'confirmado', 'compareceu', 'nao_veio'] as const;
export type StatusConvidado = (typeof STATUS_CONVIDADO)[number];
export const NOME_STATUS_CONVIDADO: Record<StatusConvidado, string> = {
  convidado: 'Convidado',
  confirmado: 'Confirmou',
  compareceu: 'Veio',
  nao_veio: 'Não veio',
};

export type SituacaoEvento = 'planejado' | 'em_andamento' | 'encerrado' | 'cancelado';
export const NOME_SITUACAO_EVENTO: Record<SituacaoEvento, string> = {
  planejado: 'Planejado',
  em_andamento: 'Acontecendo',
  encerrado: 'Encerrado',
  cancelado: 'Cancelado',
};
export function situacaoEvento(
  e: { inicio: Date | string; fim: Date | string; cancelado: boolean },
  agora = new Date(),
): SituacaoEvento {
  if (e.cancelado) return 'cancelado';
  const t = agora.getTime();
  if (t < new Date(e.inicio).getTime()) return 'planejado';
  if (t < new Date(e.fim).getTime()) return 'em_andamento';
  return 'encerrado';
}

const centavos = z.number().int().min(0).max(100_000_000_00);
const textoOpc = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
const dia = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Data inválida (AAAA-MM-DD)')
  .refine((v) => !Number.isNaN(new Date(`${v}T00:00:00Z`).getTime()), 'Data inválida');

export const eventoSalvarSchema = z
  .object({
    nome: z.string().trim().min(3, 'Dê um nome ao evento').max(80),
    tipo: z.enum(TIPOS_EVENTO).default('feirao'),
    descricao: textoOpc(1_000),
    filialId: z.string().uuid().optional(),
    inicio: z.coerce.date(),
    fim: z.coerce.date(),
    orcamentoCentavos: centavos.nullable().optional(),
    metaVendas: z.number().int().min(0).max(1_000).nullable().optional(),
    metaLucroCentavos: centavos.nullable().optional(),
    /** página do evento no site (Site Premium) com os carros participantes */
    paginaSite: z.boolean().default(false),
  })
  .refine((e) => e.fim.getTime() > e.inicio.getTime(), {
    message: 'O fim precisa ser depois do início',
    path: ['fim'],
  })
  .refine((e) => e.fim.getTime() - e.inicio.getTime() <= 92 * 86_400_000, {
    message: 'Um evento dura no máximo 3 meses',
    path: ['fim'],
  });
export type EventoSalvar = z.infer<typeof eventoSalvarSchema>;
export type EventoSalvarEntrada = z.input<typeof eventoSalvarSchema>;

export const eventoCarrosSchema = z.object({
  itens: z
    .array(
      z.object({
        veiculoId: z.string().uuid(),
        /** null = participa sem mudar o preço */
        precoPromocionalCentavos: centavos
          .refine((v) => v > 0, 'Preço deve ser maior que zero')
          .nullable(),
      }),
    )
    .max(200),
});

export const eventoGastoSchema = z.object({
  categoria: z.enum(CATEGORIAS_GASTO_EVENTO),
  descricao: z.string().trim().min(2).max(120),
  valorCentavos: centavos.refine((v) => v > 0, 'Valor deve ser maior que zero'),
  data: dia,
  fornecedor: textoOpc(120),
  /** já foi pago (sai da conta do banco na data) */
  pago: z.boolean().default(false),
});

export const convidadoSchema = z
  .object({
    clienteId: z.string().uuid().optional(),
    nome: z.string().trim().min(2).max(120).optional(),
    telefone: z.string().trim().max(20).optional(),
  })
  .refine((c) => c.clienteId || (c.nome && c.telefone), {
    message: 'Escolha um cliente ou informe nome e telefone',
  });
export const convidadoStatusSchema = z.object({ status: z.enum(STATUS_CONVIDADO) });

/** Visitante que chegou no evento: vira cliente, lead do evento (com vendedor) e "veio". */
export const visitanteEventoSchema = z.object({
  nome: z.string().trim().min(2).max(120),
  telefone: z.string().trim().min(10, 'Telefone com DDD').max(20),
  email: z.string().trim().email().max(120).nullable().optional(),
  veiculoId: z.string().uuid().nullable().optional(),
  observacao: textoOpc(500),
  consentimento: z.literal(true, { message: 'Peça a autorização para contato' }),
});

// ─────────── resultado ───────────

export interface VendaDoEvento {
  valorCentavos: number;
  custoTotalCentavos: number;
  acessoriosCentavos: number;
  /** preço anunciado antes da promoção (para medir o desconto dado) */
  precoReferenciaCentavos: number | null;
}

export interface ResultadoEvento {
  vendas: number;
  faturamentoCentavos: number;
  lucroCarrosCentavos: number;
  descontosCentavos: number;
  gastosCentavos: number;
  gastosPorCategoria: { categoria: CategoriaGastoEvento; valorCentavos: number }[];
  /** lucro dos carros vendidos − gastos do evento */
  resultadoCentavos: number;
  /** resultado ÷ gastos (1 = 100%); null sem gastos */
  roi: number | null;
  custoPorVendaCentavos: number | null;
  custoPorLeadCentavos: number | null;
  leads: number;
  convidados: number;
  confirmados: number;
  compareceram: number;
  /** vendas ÷ (leads + quem veio sem lead) */
  conversao: number | null;
  orcamentoUsado: number | null;
  metaVendasAtingida: number | null;
  metaLucroAtingida: number | null;
}

export function calcularResultadoEvento(e: {
  vendas: VendaDoEvento[];
  gastos: { categoria: CategoriaGastoEvento; valorCentavos: number }[];
  leads: number;
  convidados: { status: StatusConvidado; leadId: string | null }[];
  orcamentoCentavos: number | null;
  metaVendas: number | null;
  metaLucroCentavos: number | null;
}): ResultadoEvento {
  const faturamento = e.vendas.reduce((s, v) => s + v.valorCentavos + v.acessoriosCentavos, 0);
  const lucro = e.vendas.reduce(
    (s, v) => s + v.valorCentavos + v.acessoriosCentavos - v.custoTotalCentavos,
    0,
  );
  const descontos = e.vendas.reduce(
    (s, v) =>
      s +
      (v.precoReferenciaCentavos ? Math.max(0, v.precoReferenciaCentavos - v.valorCentavos) : 0),
    0,
  );
  const gastos = e.gastos.reduce((s, g) => s + g.valorCentavos, 0);
  const porCat = new Map<CategoriaGastoEvento, number>();
  for (const g of e.gastos)
    porCat.set(g.categoria, (porCat.get(g.categoria) ?? 0) + g.valorCentavos);
  const resultado = lucro - gastos;
  const compareceram = e.convidados.filter((c) => c.status === 'compareceu').length;
  // Quem veio e não virou lead também conta como oportunidade
  const semLead = e.convidados.filter((c) => c.status === 'compareceu' && !c.leadId).length;
  const oportunidades = e.leads + semLead;
  return {
    vendas: e.vendas.length,
    faturamentoCentavos: faturamento,
    lucroCarrosCentavos: lucro,
    descontosCentavos: descontos,
    gastosCentavos: gastos,
    gastosPorCategoria: [...porCat.entries()]
      .map(([categoria, valorCentavos]) => ({ categoria, valorCentavos }))
      .sort((a, b) => b.valorCentavos - a.valorCentavos),
    resultadoCentavos: resultado,
    roi: gastos > 0 ? resultado / gastos : null,
    custoPorVendaCentavos: e.vendas.length && gastos ? Math.round(gastos / e.vendas.length) : null,
    custoPorLeadCentavos: e.leads && gastos ? Math.round(gastos / e.leads) : null,
    leads: e.leads,
    convidados: e.convidados.length,
    confirmados: e.convidados.filter((c) => c.status === 'confirmado' || c.status === 'compareceu')
      .length,
    compareceram,
    conversao: oportunidades ? e.vendas.length / oportunidades : null,
    orcamentoUsado: e.orcamentoCentavos ? gastos / e.orcamentoCentavos : null,
    metaVendasAtingida: e.metaVendas ? e.vendas.length / e.metaVendas : null,
    metaLucroAtingida: e.metaLucroCentavos ? resultado / e.metaLucroCentavos : null,
  };
}

/** Endereço do evento a partir do nome ("Feirão de SUVs" → "feirao-de-suvs"). */
export const slugEvento = (nome: string) =>
  nome
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'evento';
